import { useEffect, type ReactElement } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { unavailableMessage } from "../../api/client.js";
import { useApiData } from "../../api/use-api-data.js";
import { queryValue } from "../../app/navigation.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { MessagePage } from "../../components/message-page.js";
import { useDetail } from "../../components/notice.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { accountApi, accountUrls } from "./urls.js";

/**
 * メールアドレスの変更（申請 → 新しいアドレス宛のリンクで確定）。
 * <b>アドレスは API の detail で受け取る</b>（URL に載せない）。
 */

/** 確認リンクを開いた画面に出す内容（<c>GET /api/account/email/confirmation</c>。ChangeEmailConfirmViewModel）。 */
export interface EmailChangeConfirmation {
    readonly email: string;
    readonly code: string;
    readonly newEmail: string;
}

/** アドレスを強めて出す（直に開いて受け取っていなければ何も出さない）。 */
const addressOf = (email: string | undefined): ReactElement | null =>
    email === undefined ? null : <span className="font-medium text-slate-900">{email}</span>;

export function ShowWaitChangeEmailPage(): ReactElement {
    const email = useDetail();

    return (
        <MessagePage
            title="確認メールをお送りしました"
            actions={<Link to={accountUrls.settings} className="btn-secondary">アカウント設定へ</Link>}
        >
            {addressOf(email)} 宛に確認メールをお送りしました。<br />
            メール内のリンクを開いて、変更を確定してください。
        </MessagePage>
    );
}

/**
 * 確認リンク（<c>?email=&amp;code=</c>）。<b>本人の申請で、まだ有効なときだけ</b>中身を出す。
 * API が 404 を返したら（使い終わった・期限が過ぎた・申請し直した・取り消した・他人のリンク）、確定できなかったときと同じ
 * 「メールアドレスを変更できませんでした」へ移る。<b>中身は何も出さない</b>ので、他人のリンクでも漏れない。
 * 履歴に確認リンクを残さないよう置き換えて移る（戻るで同じリンクを開き直さない）。
 */
export function ChangeEmailConfirmPage(): ReactElement {
    const [search] = useSearchParams();
    const query = new URLSearchParams({
        email: queryValue(search, "email") ?? "",
        code: queryValue(search, "code") ?? "",
    });
    const data = useApiData<EmailChangeConfirmation>(`${accountApi.changeEmailConfirmation}?${query.toString()}`);
    const navigate = useNavigate();
    const invalid = data.state === "failed" && data.status === 404;

    useEffect(() => {
        if (invalid) {
            void navigate(accountUrls.changeEmailFailed, { replace: true });
        }
    }, [invalid, navigate]);

    if (data.state === "failed" && !invalid) {
        // 通信できない・相手の都合。リンクが悪いとは限らないので、失敗の画面へは送らない
        return <ConfirmUnavailable />;
    }

    return data.state === "ready" ? <ChangeEmailConfirmForm confirmation={data.value} /> : <ConfirmLoading />;
}

function ConfirmLoading(): ReactElement {
    usePageMeta("メールアドレス変更の確認");

    return <div className="page max-w-lg"><p className="text-slate-500">読み込んでいます…</p></div>;
}

function ConfirmUnavailable(): ReactElement {
    usePageMeta("メールアドレス変更の確認");

    return <div className="page max-w-lg"><FormSummary messages={[unavailableMessage]} /></div>;
}

/** 最後の確認はブラウザの確認ダイアログで行う（重要な操作は最後にダイアログで確かめる。2026-10-10 に利用者が決めた）。 */
export const changeEmailDialog = (email: string, newEmail: string): string =>
    `メールアドレスを「${email}」から「${newEmail}」へ変更します。変更後は、新しいメールアドレスでログインします。よろしいですか？`;

function ChangeEmailConfirmForm({ confirmation }: { readonly confirmation: EmailChangeConfirmation }): ReactElement {
    usePageMeta("メールアドレス変更の確認");
    const cancel = useApiForm(accountApi.cancelChangeEmail);
    const confirm = useApiForm(
        accountApi.changeEmailConfirmation, undefined, changeEmailDialog(confirmation.email, confirmation.newEmail),
    );

    return (
        <div className="page max-w-lg">
            <div className="panel">
                <h1 className="page-title">メールアドレス変更の確認</h1>
                <p className="page-lead">内容をご確認のうえ、変更を確定してください。</p>

                <FormSummary messages={[...cancel.summary, ...confirm.summary]} />

                <dl className="mb-6 grid grid-cols-[5rem_1fr] gap-y-2 text-sm">
                    <dt className="text-slate-500">現在</dt>
                    <dd>{confirmation.email}</dd>
                    <dt className="text-slate-500">変更後</dt>
                    <dd className="font-medium text-slate-900">{confirmation.newEmail}</dd>
                </dl>

                <div className="alert-warning mb-7">
                    変更後は、新しいメールアドレスでログインすることになります。
                </div>

                <div className="flex gap-3">
                    <form method="post" noValidate onSubmit={cancel.onSubmit} className="grow">
                        <button type="submit" className="btn-secondary w-full" disabled={cancel.busy}>取り消す</button>
                    </form>
                    <form method="post" noValidate onSubmit={confirm.onSubmit} className="grow">
                        <input type="hidden" name="email" value={confirmation.email} />
                        <input type="hidden" name="code" value={confirmation.code} />
                        <button type="submit" className="btn-primary w-full" disabled={confirm.busy}>変更を確定する</button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export function ChangeEmailConfirmationPage(): ReactElement {
    const email = useDetail();

    return (
        <MessagePage
            title="メールアドレスを変更しました"
            actions={<Link to={accountUrls.settings} className="btn-secondary">アカウント設定へ</Link>}
        >
            今後は {addressOf(email)} でログインしてください。
        </MessagePage>
    );
}

/**
 * 確定できなかった（確認の画面を開いた後に申請が無くなった・期限が過ぎた・変更先が別のアカウントで使われた）か、
 * 確認リンクが無効だった（開いた時点で 404。上の ChangeEmailConfirmPage）。
 * <b>新規登録の失敗の画面（ConfirmEmailFailedPage）とは別</b>——やり直す道はアカウント設定からの申請。
 */
export function ChangeEmailFailedPage(): ReactElement {
    return (
        <MessagePage
            title="メールアドレスを変更できませんでした"
            actions={<Link to={accountUrls.settings} className="btn-primary">アカウント設定へ</Link>}
        >
            {/* リンクの期限は申請から 24 時間。申し直し・取り消しでも前の申請が失効する（Implem.Portal.Backend の docs/features/05 §1） */}
            確認のリンクの有効期限（24 時間）が切れているか、無効になっているか、すでに使われている可能性があります。
            あとから申請し直したり、申請を取り消したりすると、前のリンクは使えなくなります。
            <br />
            メールアドレスを変更する場合は、アカウント設定からもう一度申請してください。
        </MessagePage>
    );
}
